import { useState } from 'react';
import { Send } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import StarRating from './StarRating';
import { toast } from 'sonner';

const ReviewForm = ({ onReviewSubmit }) => {
  const [formData, setFormData] = useState({
    name: '',
    location: '',
    rating: 5,
    text: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.text || formData.rating === 0) {
      toast.error('Please fill in all fields and select a rating');
      return;
    }

    // Create review object
    const review = {
      id: Date.now().toString(),
      name: formData.name,
      location: formData.location || 'The Bahamas',
      rating: formData.rating,
      text: formData.text,
      date: new Date().toISOString().split('T')[0]
    };

    // Call parent callback
    if (onReviewSubmit) {
      onReviewSubmit(review);
    }

    toast.success('Thank you for your review! It will be published shortly.');
    
    // Reset form
    setFormData({
      name: '',
      location: '',
      rating: 5,
      text: ''
    });
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <Card className="border-red-600/30 shadow-xl">
      <CardHeader className="bg-gradient-to-r from-red-50 to-white">
        <CardTitle className="text-2xl text-center">Share Your Experience</CardTitle>
        <p className="text-center text-gray-600 text-sm">
          Help others by sharing your experience with RELAX & RIDE
        </p>
      </CardHeader>
      <CardContent className="p-6">
        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="reviewName">Your Name *</Label>
              <Input
                id="reviewName"
                placeholder="John Smith"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                required
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="reviewLocation">Location</Label>
              <Input
                id="reviewLocation"
                placeholder="Miami, FL"
                value={formData.location}
                onChange={(e) => handleChange('location', e.target.value)}
                className="mt-1"
              />
            </div>
          </div>

          <div>
            <Label className="mb-2 block">Your Rating *</Label>
            <div className="flex items-center gap-3">
              <StarRating
                rating={formData.rating}
                onRatingChange={(rating) => handleChange('rating', rating)}
                interactive={true}
                size="large"
              />
              <span className="text-gray-600 font-medium">
                {formData.rating} {formData.rating === 1 ? 'Star' : 'Stars'}
              </span>
            </div>
          </div>

          <div>
            <Label htmlFor="reviewText">Your Review *</Label>
            <Textarea
              id="reviewText"
              placeholder="Tell us about your experience with RELAX & RIDE..."
              value={formData.text}
              onChange={(e) => handleChange('text', e.target.value)}
              required
              rows={5}
              className="mt-1"
            />
            <p className="text-xs text-gray-500 mt-1">
              Minimum 20 characters ({formData.text.length}/20)
            </p>
          </div>

          <Button 
            type="submit" 
            className="w-full bg-red-600 hover:bg-red-700 text-white py-3"
            disabled={formData.text.length < 20}
          >
            <Send className="w-4 h-4 mr-2" />
            Submit Review
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

export default ReviewForm;
